import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { TranslationsMap } from './translation.types';

type SupportedLanguage = 'pl' | 'en';

const LANGUAGE_STORAGE_KEY = 'junoaudit.language';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly http = inject(HttpClient);
  private readonly languageState = signal<SupportedLanguage>(this.resolveLanguage());
  private readonly translationsState = signal<TranslationsMap>({});

  readonly language = this.languageState.asReadonly();

  async initialize(): Promise<void> {
    await this.load(this.languageState());
  }

  t(key: string): string {
    return this.translationsState()[key] ?? key;
  }

  async setLanguage(language: SupportedLanguage): Promise<void> {
    await this.load(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    this.languageState.set(language);
  }

  private async load(language: SupportedLanguage): Promise<void> {
    const translations = await firstValueFrom(
      this.http.get<TranslationsMap>(`/i18n/${language}/common.json`),
    );

    this.translationsState.set(translations);
  }

  private resolveLanguage(): SupportedLanguage {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);

    if (saved === 'pl' || saved === 'en') {
      return saved;
    }

    const browserLanguage = navigator.language.toLowerCase();
    return browserLanguage.startsWith('pl') ? 'pl' : 'en';
  }
}
